'use client';

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { createClient } from '@/lib/localData/client';

interface WorkspaceSettings {
  id?: string;
  workspaceName: string;
  logoUrl: string | null;
  brandingColor: string;
}

interface WorkspaceContextValue {
  settings: WorkspaceSettings;
  loading: boolean;
  refresh: () => Promise<void>;
  updateSettings: (updates: Partial<WorkspaceSettings>) => Promise<void>;
}

const DEFAULT_SETTINGS: WorkspaceSettings = {
  workspaceName: 'TaskFlow',
  logoUrl: null,
  brandingColor: '#6C63FF',
};

const WorkspaceContext = createContext<WorkspaceContextValue>({
  settings: DEFAULT_SETTINGS,
  loading: false,
  refresh: async () => {},
  updateSettings: async () => {},
});

export function useWorkspace() {
  return useContext(WorkspaceContext);
}

export function WorkspaceProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettings] = useState<WorkspaceSettings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const localData = createClient();

  // Reject anything that isn't a plain `#rgb` / `#rrggbb` / `#rrggbbaa`.
  // setProperty doesn't execute CSS, but allowing arbitrary strings into a
  // CSS variable opens the door to "red; background-image: url(...)" style
  // value injection. A strict regex closes the surface entirely.
  const HEX_COLOR_RE = /^#[0-9a-f]{3,8}$/i;

  const applyBrandingColor = (color: string) => {
    if (typeof document === 'undefined') return;
    if (!color || !HEX_COLOR_RE.test(color)) return;
    document.documentElement.style.setProperty('--primary', color);
    document.documentElement.style.setProperty('--ring', color);
  };

  const fetchSettings = useCallback(async () => {
    try {
      const { data } = await localData
        .from('workspace_settings')
        .select('id, workspace_name, logo_url, branding_color')
        .limit(1)
        .single();

      if (data) {
        const mapped: WorkspaceSettings = {
          id: data.id,
          workspaceName: data.workspace_name || 'TaskFlow',
          logoUrl: data.logo_url || null,
          brandingColor: data.branding_color || '#6C63FF',
        };
        setSettings(mapped);
        applyBrandingColor(mapped.brandingColor);
      }
    } catch {
      // Table may not exist yet; use defaults
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const updateSettings = useCallback(async (updates: Partial<WorkspaceSettings>) => {
    const dbUpdates: Record<string, unknown> = {};
    if (updates.workspaceName !== undefined) dbUpdates.workspace_name = updates.workspaceName;
    if (updates.logoUrl !== undefined) dbUpdates.logo_url = updates.logoUrl;
    if (updates.brandingColor !== undefined) dbUpdates.branding_color = updates.brandingColor;
    dbUpdates.updated_at = new Date().toISOString();

    const { data: existing } = await localData
      .from('workspace_settings')
      .select('id')
      .limit(1)
      .single();

    if (existing?.id) {
      await localData.from('workspace_settings').update(dbUpdates).eq('id', existing.id);
    } else {
      await localData.from('workspace_settings').insert(dbUpdates);
    }

    const newSettings = { ...settings, ...updates };
    setSettings(newSettings);
    if (updates.brandingColor) applyBrandingColor(updates.brandingColor);
  }, [settings]);

  return (
    <WorkspaceContext.Provider value={{ settings, loading, refresh: fetchSettings, updateSettings }}>
      {children}
    </WorkspaceContext.Provider>
  );
}
